import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OnboardingRedirect } from "@/components/OnboardingRedirect";
import { DashboardLayout } from "@/components/DashboardLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AttributionListener } from "@/components/AttributionListener";
import { ClarityPageTracker } from "@/components/ClarityPageTracker";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminEnhanced = lazy(() => import("./pages/AdminEnhanced"));
const ConnectDataSource = lazy(() => import("./pages/ConnectDataSource"));
const DataSources = lazy(() => import("./pages/DataSources"));
const DataSourceHealth = lazy(() => import("./pages/DataSourceHealth"));
const AlertSettings = lazy(() => import("./pages/AlertSettings"));
const Settings = lazy(() => import("./pages/Settings"));
const WorkspaceMembers = lazy(() => import("./pages/WorkspaceMembers"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const MutedPatterns = lazy(() => import("./pages/MutedPatterns"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Gdpr = lazy(() => import("./pages/Gdpr"));

const queryClient = new QueryClient();

const SuspenseFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading" />
  </div>
);

const AuthProviderWrapper = () => (
  <AuthProvider>
    <OnboardingRedirect>
      <Outlet />
    </OnboardingRedirect>
  </AuthProvider>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AttributionListener />
          <ClarityPageTracker />
          <Suspense fallback={<SuspenseFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/gdpr" element={<Gdpr />} />

              <Route element={<AuthProviderWrapper />}>
                <Route path="/auth" element={<Auth />} />
                <Route path="/invite/accept" element={<InviteAccept />} />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  element={
                    <ProtectedRoute>
                      <DashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/admin" element={<AdminEnhanced />} />
                  <Route path="/data-sources" element={<DataSources />} />
                  <Route path="/data-sources/:id/health" element={<DataSourceHealth />} />
                  <Route path="/connect-data-source" element={<ConnectDataSource />} />
                  <Route path="/alert-settings" element={<AlertSettings />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/settings/:tab" element={<Settings />} />
                  <Route path="/muted-patterns" element={<MutedPatterns />} />
                  <Route path="/workspace/members" element={<WorkspaceMembers />} />
                </Route>
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
